import React, { useCallback, useEffect, useState } from 'react';
import * as ReactBootstrap from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { IReducer } from '../../../../../../redux/configureStore';
import { getAllPartnersList } from '../../../../../../redux/fetchActions/legadoPage/clientsApp';
import { ReportRequest, TipoRelatorio } from '../../interface';
import { getDownloadUrl, listReports } from '../../services/reportsService';
import LegendaTarifas from './LegendaTarifas';
import RequestForm from './RequestForm';
import RequestsTable from './RequestsTable';
import TypeSelector from './TypeSelector';

const REFRESH_INTERVAL_MS = 5000;

const LoadingCard = styled.div`
  background-color: ${(props) => props.theme.colors.white};
  border-radius: ${(props) => props.theme.border.default};
  box-shadow: ${(props) => props.theme.shadow.default};
  padding: 3rem 1.5rem;
  margin: 0 0 1rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  box-sizing: border-box;
  width: 100%;
`;

const LoadingText = styled.p`
  color: ${(props) => props.theme.colors.lightGray};
  font-size: ${(props) => props.theme.fontSize.medium};
  margin: 0;
`;

function Home() {
  const dispatch = useDispatch();
  const [reports, setReports] = useState<ReportRequest[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  // Aba ativa do TypeSelector. Comeca em 'recebimento' (relatorio
  // historico mais usado); o usuario pode trocar pra 'tarifas' (substitui
  // o menu>Tarifas legado). O backend filtra a listagem por esse valor.
  const [tipoRelatorio, setTipoRelatorio] =
    useState<TipoRelatorio>('recebimento');

  const partnersList = useSelector(
    (reducer: IReducer) => reducer.clientsApp.partnersList
  );
  const userProfile = useSelector((reducer: IReducer) => reducer.userProfile);
  const partnerProfile = useSelector(
    (reducer: IReducer) => reducer.partnerProfile
  );

  // Aguarda o userProfile carregar (tem `partnerId` definido) antes de
  // qualquer decisao. Fidúcia precisa da partnersList carregada. Whitelabel
  // precisa do partnerProfile.idLegado.
  const userReady = userProfile.partnerId !== undefined;
  const isFiducia = userProfile.partnerId === 1;
  const fiduciaHasPartners = !!(partnersList && partnersList.length > 0);
  const whitelabelHasProfile = !!partnerProfile.idLegado;
  let partnersReady = false;
  if (userReady) {
    partnersReady = isFiducia ? fiduciaHasPartners : whitelabelHasProfile;
  }

  // Dispara getAllPartnersList do Home (e nao do PartnerSelect) para evitar
  // deadlock: o PartnerSelect so monta depois que o loading some, mas o
  // loading espera a partnersList — sem o dispatch aqui, ela nunca chegaria.
  useEffect(() => {
    if (userReady && userProfile.partnerId === 1) {
      if (!partnersList || partnersList.length === 0) {
        dispatch(getAllPartnersList());
      }
    }
  }, [dispatch, userReady, userProfile.partnerId, partnersList]);

  const reload = useCallback(async () => {
    setRefreshing(true);
    try {
      // Listagem unica: traz solicitacoes de TODOS os tipos. A tabela
      // distingue cada uma pela coluna "Tipo de Relatorio". A aba
      // selecionada controla apenas qual formulario aparece, nao o filtro
      // da tabela.
      const data = await listReports();
      setReports(data);
    } finally {
      setRefreshing(false);
      setFirstLoadDone(true);
    }
  }, []);

  useEffect(() => {
    reload();
    const interval = setInterval(reload, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [reload]);

  async function handleDownload(id: string) {
    const url = await getDownloadUrl(id);
    if (!url) {
      Swal.fire({
        icon: 'warning',
        title: 'Arquivo indisponível',
        text: 'O relatório ainda não está pronto.',
      });
      return;
    }
    window.location.assign(url);
  }

  if (!firstLoadDone || !userReady || !partnersReady) {
    return (
      <LoadingCard>
        <ReactBootstrap.Spinner animation="border" variant="info" />
        <LoadingText>Carregando solicitações e dados de parceiro…</LoadingText>
      </LoadingCard>
    );
  }

  function handleChangeTipoRelatorio(novoTipo: TipoRelatorio) {
    // A listagem e unica, entao trocar de aba so muda o formulario — nao
    // precisa recarregar nem limpar a tabela.
    setTipoRelatorio(novoTipo);
  }

  return (
    <>
      <TypeSelector
        value={tipoRelatorio}
        onChange={handleChangeTipoRelatorio}
      />
      <RequestForm onCreated={reload} tipoRelatorio={tipoRelatorio} />
      <RequestsTable
        reports={reports}
        refreshing={refreshing}
        onRefresh={reload}
        onDownload={handleDownload}
      />
      <LegendaTarifas />
    </>
  );
}

export default Home;
